import { createContext, useCallback, useContext, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { getSettings } from '../../lib/settings'

interface ToastContextValue {
  showToast: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TOAST_DURATION_MS = 1600

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null)
  const timerRef = useRef<number | null>(null)

  const showToast = useCallback((next: string) => {
    setMessage(next)
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => setMessage(null), TOAST_DURATION_MS)

    if (getSettings().haptic && typeof navigator.vibrate === 'function') {
      try {
        navigator.vibrate(30)
      } catch {
        // Vibration er ikke kritisk for toasten
      }
    }
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        className={`fixed bottom-[calc(96px+env(safe-area-inset-bottom,0px))] left-1/2 z-20 -translate-x-1/2 rounded-xl bg-cream px-4 py-2.5 font-semibold text-ink shadow-[0_10px_24px_rgba(0,0,0,.35)] transition-opacity ${
          message ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        role="status"
        aria-live="polite"
      >
        {message}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast skal bruges inden i en ToastProvider')
  }
  return ctx
}
